// src/courses/ProgressManager.js
// 🎓 TICKET ZXMQL3 - Sistema de progreso persistente con localStorage
// 🎓 TICKET HMXQD2 - Migrado a backend con localStorage como fallback
// Gestiona el progreso del usuario en los cursos con persistencia real

import { fetchCourseProgress, completeActivity as apiCompleteActivity, fetchAllCourseProgress } from './courseProgressApi';

const STORAGE_KEY = 'edutech_course_progress';

/**
 * 🎓 TICKET HMXQD2 - Cache en memoria para progreso cargado desde backend
 * Evita llamadas redundantes durante la misma sesión
 */
const progressCache = new Map();

/**
 * Obtiene todo el progreso del usuario desde localStorage (FALLBACK)
 * @returns {Object} Objeto con progreso por courseId
 */
export function getAllProgressFromLocalStorage() {
  try {
    const data = localStorage.getItem(STORAGE_KEY);
    return data ? JSON.parse(data) : {};
  } catch (error) {
    console.error('Error leyendo progreso desde localStorage:', error);
    return {};
  }
}

/**
 * Guarda todo el progreso en localStorage (FALLBACK)
 * @param {Object} progress - Objeto completo de progreso
 */
function saveProgressToLocalStorage(progress) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error('Error guardando progreso en localStorage:', error);
  }
}

/**
 * 🎓 TICKET HMXQD2 - Obtiene las actividades completadas de un curso (BACKEND FIRST)
 * Intenta primero desde backend/cache, fallback a localStorage
 * @param {string} courseId - ID del curso
 * @returns {Promise<string[]>} Array de activity IDs completados
 */
export async function getCompletedActivities(courseId) {
  // Verificar cache primero
  if (progressCache.has(courseId)) {
    console.log(`📦 [ProgressManager] Using cached progress for: ${courseId}`);
    return progressCache.get(courseId);
  }
  
  // Intentar desde backend
  const result = await fetchCourseProgress(courseId);
  
  if (result.success) {
    const completed = result.data.completedActivities || [];
    progressCache.set(courseId, completed);
    return completed;
  }
  
  // Fallback a localStorage
  console.warn(`⚠️ [ProgressManager] Backend failed, using localStorage for: ${courseId}`);
  const localProgress = getAllProgressFromLocalStorage();
  return localProgress[courseId]?.completedActivities || [];
}

/**
 * 🎓 TICKET HMXQD2 - Versión síncrona para compatibilidad (usa cache o localStorage)
 * @param {string} courseId - ID del curso
 * @returns {string[]} Array de activity IDs completados
 */
export function getCompletedActivitiesSync(courseId) {
  // Intentar desde cache primero
  if (progressCache.has(courseId)) {
    return progressCache.get(courseId);
  }
  
  // Fallback a localStorage
  const localProgress = getAllProgressFromLocalStorage();
  return localProgress[courseId]?.completedActivities || [];
}

/**
 * 🎓 TICKET HMXQD2 - Marca una actividad como completada (BACKEND FIRST)
 * Intenta primero backend, fallback a localStorage
 * @param {string} courseId - ID del curso
 * @param {string} activityId - ID de la actividad
 * @returns {Promise<boolean>} true si se guardó exitosamente
 */
export async function markActivityComplete(courseId, activityId) {
  // Intentar guardar en backend
  const result = await apiCompleteActivity(courseId, activityId);
  
  if (result.success) {
    // Actualizar cache
    const completed = result.data.completedActivities || [];
    progressCache.set(courseId, completed);
    console.log(`✅ [ProgressManager] Activity completed (backend): ${courseId} → ${activityId}`);
    return true;
  }
  
  // Fallback a localStorage
  console.warn(`⚠️ [ProgressManager] Backend failed, saving to localStorage: ${courseId}/${activityId}`);
  const progress = getAllProgressFromLocalStorage();
  
  if (!progress[courseId]) {
    progress[courseId] = {
      completedActivities: []
    };
  }
  
  if (!progress[courseId].completedActivities.includes(activityId)) {
    progress[courseId].completedActivities.push(activityId);
    saveProgressToLocalStorage(progress);
    
    // Actualizar cache también
    progressCache.set(courseId, progress[courseId].completedActivities);
    console.log(`✅ [ProgressManager] Activity completed (localStorage): ${courseId} → ${activityId}`);
  }
  
  return false; // Indica que se usó fallback
}

/**
 * Verifica si una actividad está completada
 * @param {string} courseId - ID del curso
 * @param {string} activityId - ID de la actividad
 * @returns {boolean}
 */
export function isActivityCompleted(courseId, activityId) {
  const completed = getCompletedActivitiesSync(courseId);
  return completed.includes(activityId);
}

/**
 * Calcula el progreso porcentual de un curso
 * @param {Object} course - Objeto curso con modules[]
 * @returns {number} Porcentaje de 0 a 100
 */
export function calculateCourseProgress(course) {
  if (!course.modules || course.modules.length === 0) {
    return 0;
  }
  
  // Contar total de actividades
  const totalActivities = course.modules.reduce(
    (sum, module) => sum + (module.activities?.length || 0),
    0
  );
  
  if (totalActivities === 0) {
    return 0;
  }
  
  // Usar versión síncrona para obtener completadas
  const completed = getCompletedActivitiesSync(course.id);
  const completedCount = completed.length;
  
  // Calcular porcentaje
  const percentage = Math.round((completedCount / totalActivities) * 100);
  return Math.min(percentage, 100); // Cap en 100%
}

/**
 * Obtiene la siguiente actividad recomendada (Next Best Action)
 * @param {Object} course - Objeto curso con modules[]
 * @returns {Object|null} { module, activity } o null si todo completado
 */
export function getNextActivity(course) {
  if (!course.modules || course.modules.length === 0) {
    return null;
  }
  
  const completed = getCompletedActivitiesSync(course.id);
  
  // Recorrer módulos en orden
  for (const module of course.modules) {
    if (!module.activities || module.activities.length === 0) {
      continue;
    }
    
    // Recorrer actividades en orden
    for (const activity of module.activities) {
      // Primera actividad no completada
      if (!completed.includes(activity.id)) {
        return {
          module,
          activity
        };
      }
    }
  }
  
  // Todas las actividades completadas
  return null;
}

/**
 * Limpia todo el progreso (útil para testing/reset)
 */
export function clearAllProgress() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.log('🗑️ Progreso limpiado');
  } catch (error) {
    console.error('Error limpiando progreso:', error);
  }
}

/**
 * Obtiene estadísticas de un curso
 * @param {Object} course - Objeto curso con modules[]
 * @returns {Object} { totalActivities, completedActivities, remainingActivities, percentage }
 */
export function getCourseStats(course) {
  if (!course.modules || course.modules.length === 0) {
    return {
      totalActivities: 0,
      completedActivities: 0,
      remainingActivities: 0,
      percentage: 0
    };
  }
  
  const totalActivities = course.modules.reduce(
    (sum, module) => sum + (module.activities?.length || 0),
    0
  );
  
  const completed = getCompletedActivitiesSync(course.id);
  const completedActivities = completed.length;
  const remainingActivities = totalActivities - completedActivities;
  const percentage = calculateCourseProgress(course);
  
  return {
    totalActivities,
    completedActivities,
    remainingActivities,
    percentage
  };
}

/**
 * 🎓 TICKET HMXQD2 - Pre-cargar progreso de un curso desde backend
 * Útil para llamar en useEffect antes de renderizar
 * @param {string} courseId - ID del curso
 * @returns {Promise<void>}
 */
export async function preloadCourseProgress(courseId) {
  await getCompletedActivities(courseId);
}

/**
 * 🎓 TICKET HMXQD2 - Limpiar cache (útil al hacer logout o cambiar usuario)
 */
export function clearProgressCache() {
  progressCache.clear();
  console.log('🗑️ Cache de progreso limpiado');
}
